import { useMutation, useQuery } from '@tanstack/react-query';
import { apiClient } from '../../api/client';

/**
 * Shape of `GET /api/v1/billing/status`. `status` mirrors the tenant's Stripe
 * subscription state: `None` (never subscribed), `Active`, `PastDue` (in grace),
 * or `Canceled`. Kept as a widened string union so an unexpected server value
 * never breaks the type.
 */
export interface BillingStatus {
  status: 'None' | 'Active' | 'PastDue' | 'Canceled' | (string & {});
  currentPeriodEndUtc: string | null;
  cancelAtPeriodEnd: boolean;
  planCode: string | null;
}

export interface UseBillingStatusOptions {
  /** Poll interval in ms while mounted (e.g. the success page waiting on the webhook). */
  refetchInterval?: number | false;
  /** Skip the query until true (default true). */
  enabled?: boolean;
}

/**
 * TanStack Query hook over the billing endpoints. Reads subscription status and
 * exposes a `startCheckout()` mutation that redirects the browser to Stripe
 * Checkout. The status query and the checkout POST are both gate-exempt on the
 * server, so a lapsed tenant can always reach them.
 */
export function useBillingStatus(options?: UseBillingStatusOptions) {
  const query = useQuery({
    queryKey: ['billing-status'],
    queryFn: async () => {
      const res = await apiClient.get<BillingStatus>('/api/v1/billing/status');
      return res.data;
    },
    staleTime: 10_000,
    refetchInterval: options?.refetchInterval,
    enabled: options?.enabled ?? true
  });

  const checkout = useMutation({
    mutationFn: async () => {
      // Empty body: POST action endpoints must send one to avoid 415.
      const res = await apiClient.post<{ url: string }>('/api/v1/billing/checkout-session', {});
      return res.data;
    },
    onSuccess: (data) => {
      // Hard navigation off-SPA to Stripe-hosted Checkout.
      window.location.assign(data.url);
    }
  });

  return {
    ...query,
    /** Kick off Stripe Checkout (redirects on success). */
    startCheckout: checkout.mutate,
    isStartingCheckout: checkout.isPending,
    checkoutError: checkout.error
  };
}
